/* eslint-disable no-console */
import { AnalyticsAdapter } from './analytics.adapter';
import * as T from './analytics.types';
export class AnalyticsService {
  constructor(private adapter: AnalyticsAdapter) {}

  private _initialized = false;
  private _enabled = false;

  get isInitialized() {
    return this._initialized;
  }

  get isEnabled() {
    return this._enabled;
  }

  get isClientSide() {
    return typeof window !== 'undefined';
  }

  init(token: string, options: T.AnalyticsInitOptions): void {
    const { isEnabled = false, ...rest } = options;

    this._enabled = isEnabled;

    if (!this.isEnabled) return;

    try {
      if (!this.isClientSide) {
        this.handleError(
          new Error('Service can only be initialized on client-side'),
          'init'
        );
        return;
      }

      if (!token.length || typeof token !== 'string') {
        this.handleError(new Error('Invalid analytics token provided'), 'init');
        return;
      }

      this.adapter.init(token, rest);

      this._initialized = true;
    } catch (error) {
      this._initialized = false;

      if (error instanceof Error) {
        this.handleError(error, 'init');
        return;
      }

      this.handleError(
        new Error('Error while initiating analytics service'),
        'init'
      );
    }
  }

  sendEvent(
    eventName: T.AnalyticsEventName,
    props?: T.AnalyticsSendEventProps
  ): void {
    if (!this.isEnabled) return;

    if (!this.isInitialized) {
      this.handleError(new Error('Service not initialized'), 'sendEvent');
      return;
    }

    try {
      this.adapter.sendEvent(eventName, props);
    } catch (error) {
      if (error instanceof Error) {
        this.handleError(error, 'sendEvent');
        return;
      }
      this.handleError(new Error('Error while sending event'), 'sendEvent');
    }
  }

  register(options: T.AnalyticsRegisterOptions): void {
    if (!this.isEnabled) return;

    if (!this.isInitialized) {
      this.handleError(new Error('Service not initialized'), 'register');
      return;
    }

    this.adapter.register?.(options);
  }

  identifyUser(user?: T.IdentifyUserData): void {
    if (!this.isEnabled) return;

    if (!this.isInitialized) {
      this.handleError(new Error('Service not initialized'), 'identifyUser');
      return;
    }

    if (!user || !user?.id) {
      this.handleError(new Error('User ID not provided'), 'identifyUser');
      return;
    }

    this.adapter.identifyUser?.(user);
  }

  cleanup(): void {
    if (!this.isEnabled) return;

    if (!this.isInitialized) {
      this.handleError(new Error('Service not initialized'), 'cleanup');
      return;
    }

    this.adapter.cleanup();
    this._initialized = false;
  }

  private handleError(error: Error, context: string): void {
    const errorMessage = `< [Analytics${context ? `:${context}` : ''}] ${
      error.message
    } >`;
    console.error(errorMessage, error);
  }
}
